import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, ChevronDown, ChevronUp, X, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'mbt_cookie_consent';

const defaultConsent = {
  necessary: true,   // always true, cannot be toggled
  analytics: false,
  preferences: false,
};

export function getCookieConsent() {
  try {
    const stored = localStorage?.getItem(CONSENT_KEY);
    if (stored) return JSON.parse(stored);
  } catch (_) {}
  return null;
}

export function saveCookieConsent(consent) {
  try {
    localStorage?.setItem(CONSENT_KEY, JSON.stringify({ ...consent, savedAt: new Date()?.toISOString() }));
  } catch (_) {}
}

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({ ...defaultConsent });

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      // Small delay so the page renders first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const applyConsent = (finalConsent) => {
    saveCookieConsent(finalConsent);
    // Update GA4 consent state
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: finalConsent?.analytics ? 'granted' : 'denied',
        functionality_storage: finalConsent?.preferences ? 'granted' : 'denied',
      });
    }
    // If analytics just granted, load GA script and fire a page_view for the current page
    if (finalConsent?.analytics) {
      const GA_MEASUREMENT_ID = import.meta.env?.VITE_GA_MEASUREMENT_ID;
      if (GA_MEASUREMENT_ID && !window.__gaScriptLoaded) {
        window.__gaScriptLoaded = true;
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        script.async = true;
        script.onload = () => {
          if (typeof window.gtag === 'function') {
            window.gtag('js', new Date());
            window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
            window.gtag('consent', 'update', { analytics_storage: 'granted' });
            window.gtag('event', 'page_view', {
              page_path: window.location?.pathname + window.location?.search,
              page_title: document.title,
            });
          }
        };
        document.head?.appendChild(script);
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: window.location?.pathname + window.location?.search,
          page_title: document.title,
        });
      }
    }
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, preferences: true };
    setConsent(all);
    applyConsent(all);
  };

  const handleRejectAll = () => {
    const minimal = { necessary: true, analytics: false, preferences: false };
    setConsent(minimal);
    applyConsent(minimal);
  };

  const handleSaveChoices = () => {
    applyConsent(consent);
  };

  const toggle = (key) => {
    if (key === 'necessary') return; // cannot toggle necessary
    setConsent(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const cookieCategories = [
    {
      key: 'necessary',
      label: 'Necessary',
      description: 'Essential for the website to function. Cannot be disabled. Includes authentication session cookies (Supabase) and security tokens.',
      required: true,
    },
    {
      key: 'analytics',
      label: 'Analytics',
      description: 'Help us understand how visitors interact with our app (Google Analytics 4). No personally identifiable information is collected.',
      required: false,
    },
    {
      key: 'preferences',
      label: 'Preferences',
      description: 'Remember your settings and personalisation choices (e.g. language, display preferences) across sessions.',
      required: false,
    },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          className="fixed bottom-0 left-0 right-0 z-[200] p-3 sm:p-4"
          role="dialog"
          aria-label="Cookie consent"
          aria-modal="true"
        >
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Cookie className="w-4 h-4 text-primary" />
                </div>
                <h2 className="text-base font-bold text-foreground">We use cookies</h2>
              </div>
              <button
                onClick={handleRejectAll}
                aria-label="Reject all and close"
                className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 mt-0.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 pb-3">
              <p className="text-sm text-muted-foreground leading-relaxed">
                We use cookies to keep you signed in, understand how you use our app, and remember your preferences.
                Read our{' '}
                <Link to="/privacy-policy" className="text-primary underline underline-offset-2 hover:text-primary/80 font-medium">
                  Privacy Policy
                </Link>{' '}
                to learn more.
              </p>

              {/* Toggle details */}
              <button
                onClick={() => setShowDetails(prev => !prev)}
                className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                aria-expanded={showDetails}
              >
                {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showDetails ? 'Hide cookie settings' : 'Manage cookie settings'}
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 space-y-3 border-t border-border pt-3">
                      {cookieCategories?.map(cat => (
                        <div key={cat?.key} className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-foreground">{cat?.label}</span>
                              {cat?.required && (
                                <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-medium">Always on</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">{cat?.description}</p>
                          </div>
                          {/* Toggle switch */}
                          <button
                            role="switch"
                            aria-checked={consent?.[cat?.key]}
                            aria-label={`Toggle ${cat?.label} cookies`}
                            disabled={cat?.required}
                            onClick={() => toggle(cat?.key)}
                            className={`flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 mt-0.5 ${
                              consent?.[cat?.key] ? 'bg-primary' : 'bg-gray-300'
                            } ${cat?.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                consent?.[cat?.key] ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 pt-2 flex flex-col sm:flex-row gap-2 sm:gap-3">
              {showDetails ? (
                <>
                  <button
                    onClick={handleSaveChoices}
                    className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm transition-all hover:bg-primary/90"
                  >
                    Save my choices
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-2.5 border border-primary text-primary rounded-lg font-semibold text-sm transition-all hover:bg-primary/10"
                  >
                    Accept all
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm transition-all hover:bg-primary/90"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-4 py-2.5 border border-border text-muted-foreground rounded-lg font-semibold text-sm transition-all hover:bg-muted/50"
                  >
                    Reject non-essential
                  </button>
                </>
              )}
            </div>

            {/* Footer note */}
            <div className="px-5 pb-4 flex items-center gap-1.5">
              <Shield className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                Your choices are saved locally and can be changed at any time in{' '}
                <Link to="/settings-and-privacy" className="underline underline-offset-1 hover:text-foreground">Settings & Privacy</Link>.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
